/**
 * APP Plus module landing (P7, enriched 2026-09-11 — owner-approved plan):
 * navigation first, then composite / savings / path-to-QPS, then the measure
 * scorecard + "Do next", then data health + roster exposure. Everything reads
 * data the module already computes (useAppPlusScoring, roster-derived, upload
 * history, the Patients worklist) — no new backend.
 */
import { useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight, SlidersHorizontal, AlertTriangle, LineChart, UploadCloud, Users,
  CheckCircle2, XCircle, MinusCircle, CircleDashed, ClipboardList,
} from 'lucide-react';
import { useProgramAccess } from '@/platform/ProgramAccessContext';
import { useAco } from '@/contexts/AcoContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { apiClient } from '@/lib/api-client';
import { logicForYear } from '@/lib/app-plus/years/registry';
import type { MeasureScoreResult } from '@/lib/app-plus-scoring';
import { useAppPlusScoring } from './lib/useAppPlusScoring';
import { AcoHeader, NoAcoState, YearUnsupportedState } from './components/AcoHeader';
import { RosterExposureCard } from './components/RosterExposureCard';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const int = new Intl.NumberFormat('en-US');

interface UploadRow {
  id: string;
  fileName: string;
  uploadDate: string;
  status: string;
  recordsProcessed: number;
  collectionType?: string | null;
}

type Bucket = 'clinical' | 'cahps' | 'adminClaims';

interface ScorecardRow {
  measureId: string;
  name: string;
  bucket: Bucket;
  result: MeasureScoreResult | null;
  /** Pipeline id + method for the drill-down (clinical lanes with data only). */
  drill?: { fetchId: string; method: string } | null;
}

interface NextAction {
  title: string;
  hint: string;
  to: string;
}

function stateIcon(result: MeasureScoreResult | null) {
  if (!result) return <CircleDashed className="h-4 w-4 text-slate-400" aria-label="Not entered" />;
  switch (result.state) {
    case 'scored':
      return <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-label="Scored" />;
    case 'dc_miss':
      return <AlertTriangle className="h-4 w-4 text-amber-600" aria-label="Fails data completeness" />;
    case 'unreported':
      return <CircleDashed className="h-4 w-4 text-slate-400" aria-label="Not reported" />;
    case 'excluded_case_minimum':
    case 'excluded_no_benchmark':
      return <MinusCircle className="h-4 w-4 text-slate-500" aria-label="Excluded" />;
    default:
      return <XCircle className="h-4 w-4 text-slate-400" />;
  }
}

function stateLabel(result: MeasureScoreResult | null): string {
  if (!result) return 'Not entered';
  switch (result.state) {
    case 'scored': return result.displayState === 'official' ? 'Official' : 'Projected';
    case 'dc_miss': return 'Fails DC';
    case 'unreported': return 'Not reported';
    case 'excluded_case_minimum': return 'Below case min';
    case 'excluded_no_benchmark': return 'No benchmark';
    default: return result.state;
  }
}

function pathwayGlyph(status: string) {
  if (status === 'met') return <span className="font-semibold text-emerald-600">✓</span>;
  if (status === 'projected') return <span className="font-semibold text-blue-600">◔</span>;
  if (status === 'unknowable') return <span className="font-semibold text-amber-600">?</span>;
  return <span className="font-semibold text-slate-400">○</span>;
}

export default function AppPlusOverview() {
  const navigate = useNavigate();
  const { hasProgram, loading: accessLoading } = useProgramAccess();
  const { selectedAco, loading: acoLoading } = useAco();
  const { programYear } = useProgramYear();
  const scoring = useAppPlusScoring(selectedAco?.id ?? null, selectedAco?.shadow_practice_id ?? null);
  const shadowId = selectedAco?.shadow_practice_id ?? null;

  const logic = useMemo(() => {
    try { return scoring.yearSupported ? logicForYear(programYear) : null; } catch { return null; }
  }, [programYear, scoring.yearSupported]);
  const constants = useMemo(() => {
    try { return logic?.getConstants(programYear) ?? null; } catch { return null; }
  }, [logic, programYear]);
  const measureSet = useMemo(() => {
    try { return logic?.getMeasureSet(programYear) ?? null; } catch { return null; }
  }, [logic, programYear]);

  const score = scoring.applied;

  // ----- Scorecard rows: one per composite slot, in measure-set order -----
  const rows = useMemo<ScorecardRow[]>(() => {
    if (!measureSet) return [];
    const byId = new Map((score?.measures ?? []).map(m => [m.measureId, m]));
    return measureSet.measures.map(m => {
      const lanes = scoring.calculated[m.measureId] ?? {};
      const result = byId.get(m.measureId) ?? null;
      // Prefer the lane the engine scored; else any lane with data.
      const laneKey = result?.collectionType && lanes[result.collectionType]
        ? result.collectionType
        : Object.keys(lanes)[0];
      const lane = laneKey ? lanes[laneKey] : undefined;
      return {
        measureId: m.measureId,
        name: m.name,
        bucket: m.bucket as Bucket,
        result,
        drill: lane ? { fetchId: lane.pipelineMeasureId ?? m.measureId, method: lane.submissionMethod ?? laneKey! } : null,
      };
    });
  }, [measureSet, score, scoring.calculated]);

  // ----- Do next: ranked from measure states + config gaps -----
  const nextActions = useMemo<NextAction[]>(() => {
    const out: NextAction[] = [];
    if (!score) return out;
    const dcMisses = rows.filter(r => r.bucket === 'clinical' && r.result?.state === 'dc_miss');
    for (const r of dcMisses) {
      out.push({
        title: `Close the data-completeness gap on ${r.measureId}`,
        hint: `${r.name} — ${r.result?.dataCompletenessPct?.toFixed(1) ?? '—'}% complete; ${constants?.dataCompletenessThreshold ?? 75}% required. Work the patient list.`,
        to: '/patients',
      });
    }
    const unreportedClinical = rows.filter(r => r.bucket === 'clinical' && (!r.result || r.result.state === 'unreported'));
    if (unreportedClinical.length > 0) {
      out.push({
        title: `Report ${unreportedClinical.map(r => r.measureId).join(', ')}`,
        hint: 'Unreported clinical measures score 0 and stay in available points. Upload claims or enter counts in QPS Management.',
        to: unreportedClinical.length === rows.filter(r => r.bucket === 'clinical').length ? '/upload' : '/programs/app-plus/qps',
      });
    }
    const cahps = rows.find(r => r.bucket === 'cahps');
    if (cahps && (!cahps.result || cahps.result.state === 'unreported')) {
      out.push({
        title: 'Enter CAHPS vendor top-box rates',
        hint: 'Measure 321 scores 0 until the survey SSMs are entered in Configuration → CAHPS Entry.',
        to: '/programs/app-plus/configuration',
      });
    }
    const claims = rows.filter(r => r.bucket === 'adminClaims' && (!r.result || r.result.state === 'unreported'));
    if (claims.length > 0) {
      out.push({
        title: `Set ${claims.map(r => r.measureId).join(' / ')} admin-claims inputs`,
        hint: 'CMS computes these; choose prior-year or assumed parameters in QPS Management so the composite is complete.',
        to: '/programs/app-plus/qps',
      });
    }
    if (scoring.engineConfig.savingsPoolDollars == null) {
      out.push({
        title: 'Set the shared-savings pool',
        hint: 'Enter the pool in Configuration to project dollars from the effective sharing rate.',
        to: '/programs/app-plus/configuration',
      });
    }
    if (out.length === 0) {
      out.push({
        title: 'All inputs present',
        hint: score.meetsQps ? 'Composite meets the QPS. Review the measure sheet for cheap points.' : 'Review measure points on QPS Management to close the QPS gap.',
        to: '/programs/app-plus/qps',
      });
    }
    return out.slice(0, 5);
  }, [rows, score, constants, scoring.engineConfig.savingsPoolDollars]);

  // ----- Data health: last upload + open-gap patients for the shadow practice -----
  const uploadsQ = useQuery<UploadRow[]>({
    queryKey: ['upload-history', 'all', 'all', shadowId],
    enabled: shadowId != null,
    queryFn: async () => {
      const res = await apiClient.get(`/api/claims/upload-history?practiceId=${shadowId}`);
      return (res?.data ?? res) as UploadRow[];
    },
    staleTime: 60_000,
  });
  const lastUpload = useMemo(() => {
    const list = (uploadsQ.data ?? []).filter(u => u.status !== 'deleted');
    return list.sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime())[0] ?? null;
  }, [uploadsQ.data]);

  const laneIds = useMemo(() => {
    const ids = new Set<string>();
    for (const byType of Object.values(scoring.calculated)) {
      for (const c of Object.values(byType ?? {})) if (c?.pipelineMeasureId) ids.add(c.pipelineMeasureId);
    }
    return Array.from(ids).sort();
  }, [scoring.calculated]);

  const patientsQ = useQuery<{ total: number }>({
    queryKey: ['app-plus', 'overview', 'open-gap-patients', shadowId, programYear, laneIds.join(',')],
    enabled: shadowId != null && laneIds.length > 0,
    queryFn: () => apiClient.get(
      `/api/patient-registry/${shadowId}/patients?year=${programYear}&page=1&pageSize=1&status=open&chase=not_complete&sort=gaps_desc&measureIds=${encodeURIComponent(laneIds.join(','))}`,
    ),
    staleTime: 60_000,
  });

  if (accessLoading) return null;
  if (!hasProgram('app-plus')) return <Navigate to="/" replace />;
  if (!scoring.yearSupported) return <YearUnsupportedState year={programYear} />;
  if (acoLoading || scoring.loading) return <div className="p-8 text-muted-foreground">Loading APP Plus…</div>;
  if (!selectedAco) return <NoAcoState />;

  const qps = constants?.qpsThreshold ?? null;
  const compositePct = score ? Math.max(0, Math.min(100, score.composite)) : 0;
  const twoSided = scoring.engineConfig.track !== 'BASIC_A' && scoring.engineConfig.track !== 'BASIC_B';

  return (
    <div>
      <AcoHeader title="APP Plus" subtitle="MSSP ACO reporting" />

      {/* Navigation first (matches the MIPS overview). */}
      <div className="mb-6">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Navigate</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Button variant="outline" className="justify-between" onClick={() => navigate('/programs/app-plus/qps')}>
            <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> QPS Management</span>
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="justify-between" onClick={() => navigate('/programs/app-plus/configuration')}>
            <span className="flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" /> Configuration</span>
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="justify-between" onClick={() => navigate('/programs/app-plus/insights')}>
            <span className="flex items-center gap-2"><LineChart className="h-4 w-4" /> Insights</span>
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="justify-between" onClick={() => navigate('/upload')}>
            <span className="flex items-center gap-2"><UploadCloud className="h-4 w-4" /> Upload Data</span>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Headline cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="cursor-pointer transition-shadow hover:shadow-md" onClick={() => navigate('/programs/app-plus/qps')}>
          <CardHeader className="pb-2">
            <CardDescription>Quality composite</CardDescription>
            <CardTitle className="text-4xl tabular-nums">
              {score ? score.composite.toFixed(2) : '—'}
              {score && <span className="ml-1 text-base font-normal text-muted-foreground">/ 100</span>}
              {score?.floorApplied && <Badge variant="secondary" className="ml-2 align-middle">FLOORED</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {score && qps != null && (
              <div className="mb-2">
                <div className="relative h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full ${score.meetsQps ? 'bg-emerald-500' : 'bg-blue-500'}`}
                    style={{ width: `${compositePct}%` }}
                  />
                  <div
                    className="absolute top-0 h-full w-0.5 bg-slate-700"
                    style={{ left: `${qps}%` }}
                    title={`QPS ${qps}`}
                  />
                </div>
                <div className="mt-1 flex justify-between text-[11px]">
                  <span>{score.measurePointsSum.toFixed(1)} of {score.availablePoints} measure points</span>
                  <span>QPS {qps}</span>
                </div>
              </div>
            )}
            {score
              ? (score.meetsQps ? 'Meets the QPS.' : `Does not meet the QPS yet (${(qps ?? 0) - score.composite > 0 ? ((qps ?? 0) - score.composite).toFixed(2) : '0'} points short).`)
              : 'Enter measure data to compute.'}
            {score && <span className="block">Floor: {score.floorApplied ? 'applied' : score.floorArmed ? 'armed' : 'not armed'}</span>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Shared savings</CardDescription>
            <CardTitle className="text-4xl tabular-nums">
              {score ? `${score.effectiveSharingRatePct.toFixed(1)}%` : '—'}
              {score && <span className="ml-1 text-base font-normal text-muted-foreground">sharing rate</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-0.5 text-sm text-muted-foreground">
            {score?.projectedSharedSavings != null && scoring.engineConfig.savingsPoolDollars != null ? (
              <span className="block">
                ≈ <span className="font-medium text-foreground">{usd.format(score.projectedSharedSavings)}</span>
                {' '}of {usd.format(scoring.engineConfig.savingsPoolDollars)} pool
              </span>
            ) : (
              <span className="block">Savings pool not configured.</span>
            )}
            {score && (
              <span className="block">
                Track {scoring.engineConfig.track.replace('_', ' ')} · cap {score.paymentCapPct}%
                {twoSided && score.lossRatePct != null ? ` · loss rate ${score.lossRatePct.toFixed(1)}%` : ''}
              </span>
            )}
          </CardContent>
        </Card>

        <Card className="cursor-pointer transition-shadow hover:shadow-md" onClick={() => navigate('/programs/app-plus/qps')}>
          <CardHeader className="pb-2">
            <CardDescription>Path to QPS</CardDescription>
            <CardTitle className="pt-1 text-sm font-normal">
              {score ? (
                <span className="flex flex-col gap-1.5">
                  {([['Pathway 1', score.pathway1], ['Pathway 2', score.pathway2], ['Alt standard', score.alternative]] as const).map(([label, p]) => (
                    <span key={label} title={p.reason} className="flex items-center gap-2">
                      {pathwayGlyph(p.status)}
                      <span className="font-medium text-foreground">{label}</span>
                      <span className="truncate text-xs text-muted-foreground">{p.status.replace('_', ' ')}</span>
                    </span>
                  ))}
                </span>
              ) : '—'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {score
              ? <span className="block truncate" title={score.pathway1.reason}>Next gate: {score.pathway1.status === 'met' ? 'met' : score.pathway1.reason}</span>
              : 'Full detail on the QPS Management page ("Path to QPS").'}
          </CardContent>
        </Card>
      </div>

      {/* Scorecard + Do next */}
      <div className="mt-6 grid gap-4 lg:grid-cols-[1fr_380px]">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Measure scorecard</CardDescription>
            <CardTitle className="text-base">
              {measureSet?.compositeSlots ?? 8} slots · {score ? `${score.measures.filter(m => m.state === 'scored').length} scored` : 'no data yet'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Measure</th>
                  <th className="px-2 py-2 font-medium">Type</th>
                  <th className="px-2 py-2 text-right font-medium">Rate</th>
                  <th className="px-2 py-2 text-right font-medium">DC</th>
                  <th className="px-2 py-2 text-right font-medium">Pts</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const res = r.result;
                  const clickable = !!r.drill && shadowId != null;
                  return (
                    <tr
                      key={r.measureId}
                      className={`border-b last:border-0 ${clickable ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                      onClick={clickable
                        ? () => navigate(`/programs/app-plus/measure/${encodeURIComponent(`${r.drill!.fetchId}:${r.drill!.method}`)}?evaluationType=GROUP&practiceId=${shadowId}`)
                        : undefined}
                      title={res?.detail}
                    >
                      <td className="px-4 py-2">
                        <span className="font-mono font-semibold">{r.measureId}</span>
                        <span className="ml-2 text-muted-foreground">{r.name.length > 44 ? `${r.name.slice(0, 44)}…` : r.name}</span>
                      </td>
                      <td className="px-2 py-2 text-xs text-muted-foreground">
                        {res?.collectionType ?? (r.bucket === 'cahps' ? 'CAHPS' : r.bucket === 'adminClaims' ? 'Admin claims' : '—')}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">{res?.rate != null ? `${res.rate.toFixed(1)}%` : '—'}</td>
                      <td className={`px-2 py-2 text-right tabular-nums ${res?.state === 'dc_miss' ? 'font-semibold text-amber-600' : ''}`}>
                        {res?.dataCompletenessPct != null ? `${res.dataCompletenessPct.toFixed(0)}%` : '—'}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold tabular-nums">
                        {res && (res.state === 'scored' || res.state === 'dc_miss' || res.state === 'unreported') ? res.points.toFixed(1) : '—'}
                      </td>
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center gap-1.5 text-xs">{stateIcon(res)} {stateLabel(res)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Do next</CardDescription>
            <CardTitle className="text-base">Highest-leverage inputs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {nextActions.map((a, i) => (
              <button
                key={a.title}
                type="button"
                onClick={() => navigate(a.to)}
                className="flex w-full items-start gap-3 rounded-md border px-3 py-2 text-left transition-colors hover:bg-slate-50"
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-semibold text-white">{i + 1}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{a.title}</span>
                  <span className="block text-xs text-muted-foreground">{a.hint}</span>
                </span>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Data health + roster */}
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Data health</CardDescription>
            <CardTitle className="text-base">Pipeline & patients</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <dl className="grid grid-cols-[130px_1fr] gap-y-1.5">
              <dt className="text-muted-foreground">Last upload</dt>
              <dd className="min-w-0">
                {uploadsQ.isPending && !uploadsQ.data ? (
                  <span className="inline-block h-4 w-40 animate-pulse rounded bg-muted align-middle" />
                ) : lastUpload ? (
                  <>
                    <span className="block truncate font-medium" title={lastUpload.fileName}>{lastUpload.fileName}</span>
                    <span className="block text-xs text-muted-foreground">
                      {new Date(lastUpload.uploadDate).toLocaleDateString()} · {int.format(lastUpload.recordsProcessed ?? 0)} encounters
                    </span>
                  </>
                ) : (
                  <span className="text-muted-foreground">No uploads for this ACO</span>
                )}
              </dd>
              <dt className="text-muted-foreground">Pipeline</dt>
              <dd>
                {lastUpload ? (
                  lastUpload.status === 'completed'
                    ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Phase 3 complete</span>
                    : lastUpload.status === 'failed'
                      ? <span className="inline-flex items-center gap-1 text-red-700"><XCircle className="h-3.5 w-3.5" /> Failed — see Upload History</span>
                      : <span className="inline-flex items-center gap-1 text-blue-700"><CircleDashed className="h-3.5 w-3.5" /> {lastUpload.status.replace('phase', 'Phase ')} in progress</span>
                ) : '—'}
              </dd>
              <dt className="text-muted-foreground">Collection type</dt>
              <dd>{lastUpload?.collectionType ?? (laneIds.length ? 'Untyped' : '—')}</dd>
              <dt className="text-muted-foreground">Open-gap patients</dt>
              <dd>
                {laneIds.length === 0 ? (
                  <span className="text-muted-foreground">No calculated measures yet</span>
                ) : patientsQ.isPending && !patientsQ.data ? (
                  <span className="inline-block h-4 w-24 animate-pulse rounded bg-muted align-middle" />
                ) : (
                  <span className="font-medium">{int.format(patientsQ.data?.total ?? 0)}</span>
                )}
                {laneIds.length > 0 && <span className="text-xs text-muted-foreground"> across {laneIds.length} measures</span>}
              </dd>
            </dl>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate('/upload')}>
                <UploadCloud className="mr-1.5 h-3.5 w-3.5" /> Upload History
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/patients')}>
                <ClipboardList className="mr-1.5 h-3.5 w-3.5" /> Patients
              </Button>
            </div>
          </CardContent>
        </Card>

        <RosterExposureCard />
      </div>

      <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Users className="h-3.5 w-3.5" /> Scores are projected from vendor and pipeline data until CMS official values arrive.
      </p>
    </div>
  );
}
